import { useState } from 'react';
import { usersApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, User, Mail, CreditCard, AlertCircle, Phone } from 'lucide-react';

export default function EditUserModal({ user, onClose, onUpdated }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [fullName, setFullName] = useState(user.full_name || '');
    const [email, setEmail] = useState(user.email || '');
    const [fiscalCode, setFiscalCode] = useState(user.fiscal_code || '');
    const [phone, setPhone] = useState(user.phone || '');
    const [newPassword, setNewPassword] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!fullName.trim() || !email.trim()) {
            setError('Nome ed email sono obbligatori');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            const updateData = {
                full_name: fullName.trim(),
                email: email.trim(),
                fiscal_code: fiscalCode.toUpperCase().trim(),
                phone: phone.trim()
            };
            
            // Se è stata inserita una nuova password, includila
            if (newPassword.trim()) {
                updateData.password = newPassword;
            }
            
            await usersApi.update(user.id, updateData);
            onUpdated();
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nel salvataggio');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80" onClick={onClose} />
            
            {/* Modal */}
            <div className="relative bg-[#0A0A0A] border border-white/20 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                        <User className="w-5 h-5 text-[#00FF41]" />
                        Modifica Utente
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-white/10 transition-colors"
                    >
                        <X className="w-5 h-5 text-white/60" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500 p-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                            <span className="text-red-500 text-sm">{error}</span>
                        </div>
                    )}

                    <div>
                        <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                            <User className="w-3 h-3" /> Nome Completo *
                        </Label>
                        <Input
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Nome e Cognome"
                            required
                            data-testid="edit-user-fullname"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                            <Mail className="w-3 h-3" /> Email *
                        </Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="email@esempio.it"
                            required
                            data-testid="edit-user-email"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                            <Phone className="w-3 h-3" /> Telefono (per SMS)
                        </Label>
                        <Input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+39 320 1234567"
                            data-testid="edit-user-phone"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                        <p className="text-white/40 text-xs mt-1">Formato internazionale con prefisso (es. +39...)</p>
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs flex items-center gap-1">
                            <CreditCard className="w-3 h-3" /> Codice Fiscale
                        </Label>
                        <Input
                            value={fiscalCode}
                            onChange={(e) => setFiscalCode(e.target.value.toUpperCase())}
                            placeholder="RSSMRA80A01H501U"
                            maxLength={16}
                            data-testid="edit-user-fiscal-code"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1 font-mono uppercase"
                        />
                    </div>

                    <div>
                        <Label className="text-white/60 uppercase text-xs">
                            Nuova Password (lascia vuoto per non modificare)
                        </Label>
                        <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="••••••••"
                            data-testid="edit-user-password"
                            className="bg-black border-white/20 text-white focus:border-[#00FF41] rounded-none mt-1"
                        />
                    </div>

                    <div className="text-white/40 text-xs bg-white/5 p-2 border border-white/10">
                        Ruolo: <span className="text-[#00FF41] font-mono">{user.role === 'admin' ? 'AMMINISTRATORE' : 'AGENTE'}</span>
                        <br />
                        Il ruolo non può essere modificato.
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-white/20 text-white hover:bg-white/10 rounded-none"
                        >
                            ANNULLA
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="submit-edit-user-btn"
                            className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                        >
                            {loading ? 'SALVATAGGIO...' : 'SALVA'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
